import {
  KoCharts,
  KoLoader,
  KoSkeleton,
  // KoText,
  KoTitle
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { MonitoringAPI } from '../services/monitoring.service';
import { errorRateChart } from '../charts/errorRateChart';
import { alertTimelineChart } from '../charts/alertTimelineChart';
import { incidentTrendChart } from '../charts/incidentTrendChart';
import MonitoringSubNav from './MonitoringSubNav';
import styles from './monitoring.module.scss';

const MOCK_DASHBOARD = {
  summary: { totalServices: 24, healthy: 19, degraded: 3, critical: 2, activeAlerts: 7, openIncidents: 3 },
  errorRateTimeline: [
    { timestamp: '00:00', http4xx: 12, http5xx: 3, timeout: 1, connectionError: 0 },
    { timestamp: '02:00', http4xx: 8, http5xx: 2, timeout: 0, connectionError: 1 },
    { timestamp: '04:00', http4xx: 5, http5xx: 1, timeout: 0, connectionError: 0 },
    { timestamp: '06:00', http4xx: 15, http5xx: 4, timeout: 2, connectionError: 1 },
    { timestamp: '08:00', http4xx: 28, http5xx: 8, timeout: 3, connectionError: 2 },
    { timestamp: '10:00', http4xx: 35, http5xx: 12, timeout: 5, connectionError: 3 },
    { timestamp: '12:00', http4xx: 22, http5xx: 6, timeout: 2, connectionError: 1 },
    { timestamp: '14:00', http4xx: 18, http5xx: 4, timeout: 1, connectionError: 0 },
    { timestamp: '16:00', http4xx: 14, http5xx: 3, timeout: 1, connectionError: 1 },
    { timestamp: '18:00', http4xx: 20, http5xx: 5, timeout: 2, connectionError: 1 },
    { timestamp: '20:00', http4xx: 10, http5xx: 2, timeout: 1, connectionError: 0 },
    { timestamp: '22:00', http4xx: 6, http5xx: 1, timeout: 0, connectionError: 0 }
  ],
  alertTimeline: [
    { timestamp: '00:15', severity: 'low', alertName: 'Low Throughput Alert' },
    { timestamp: '04:30', severity: 'critical', alertName: 'High Error Rate — api-gateway' },
    { timestamp: '04:45', severity: 'medium', alertName: 'Slow Response Time — search-engine' },
    { timestamp: '08:10', severity: 'critical', alertName: 'Memory Usage Critical — search-engine' },
    { timestamp: '08:32', severity: 'critical', alertName: 'High Error Rate — payment-processor' },
    { timestamp: '08:50', severity: 'high', alertName: 'CPU Spike — analytics-pipeline' },
    { timestamp: '10:00', severity: 'medium', alertName: 'Connection Pool Warning — cache-layer' },
    { timestamp: '12:15', severity: 'low', alertName: 'Latency Increase — notification-hub' },
    { timestamp: '14:00', severity: 'info', alertName: 'Scheduled Maintenance Window' },
    { timestamp: '16:30', severity: 'medium', alertName: 'Error Rate Elevated — auth-service' },
    { timestamp: '20:00', severity: 'high', alertName: 'Database Connections Spike' }
  ],
  incidentTrend: [
    { period: 'Feb 18', sev1: 0, sev2: 1, sev3: 2, sev4: 3 },
    { period: 'Feb 19', sev1: 1, sev2: 0, sev3: 3, sev4: 2 },
    { period: 'Feb 20', sev1: 0, sev2: 2, sev3: 1, sev4: 4 },
    { period: 'Feb 21', sev1: 0, sev2: 1, sev3: 2, sev4: 1 },
    { period: 'Feb 22', sev1: 1, sev2: 1, sev3: 0, sev4: 2 },
    { period: 'Feb 23', sev1: 0, sev2: 0, sev3: 2, sev4: 3 },
    { period: 'Feb 24', sev1: 0, sev2: 1, sev3: 1, sev4: 1 }
  ]
};

const STAT_CARDS = [
  { key: 'totalServices', label: 'Total Services', icon: 'AppstoreOutlined', bg: 'var(--palette-primary-lighter)' },
  { key: 'healthy', label: 'Healthy', icon: 'CheckCircleOutlined', bg: 'rgba(82, 196, 26, 0.1)' },
  { key: 'degraded', label: 'Degraded', icon: 'WarningOutlined', bg: 'rgba(250, 173, 20, 0.1)' },
  { key: 'critical', label: 'Critical', icon: 'CloseCircleOutlined', bg: 'rgba(245, 34, 45, 0.1)' },
  { key: 'activeAlerts', label: 'Active Alerts', icon: 'AlertOutlined', bg: 'rgba(250, 84, 28, 0.1)' },
  { key: 'openIncidents', label: 'Open Incidents', icon: 'ThunderboltOutlined', bg: 'rgba(114, 46, 209, 0.1)' }
];

function MonitoringOverview() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [dashboard, setDashboard] = useState({});
  const [errorData, setErrorData] = useState([]);
  const [alertData, setAlertData] = useState([]);
  const [incidentData, setIncidentData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [chartsLoading, setChartsLoading] = useState(true);

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Monitoring' }
    ]));
    fetchDashboard();
  }, [workspaceId]);

  const fetchDashboard = () => {
    setLoading(true);
    setChartsLoading(true);
    MonitoringAPI.getMonitoringDashboard().then(
      (data) => {
        const d = data?.summary ? data : MOCK_DASHBOARD;
        setDashboard(d?.summary || d || {});
        setErrorData(d?.errorRateTimeline || []);
        setAlertData(d?.alertTimeline || []);
        setIncidentData(d?.incidentTrend || []);
        setLoading(false);
        setChartsLoading(false);
      },
      () => {
        setDashboard(MOCK_DASHBOARD.summary);
        setErrorData(MOCK_DASHBOARD.errorRateTimeline);
        setAlertData(MOCK_DASHBOARD.alertTimeline);
        setIncidentData(MOCK_DASHBOARD.incidentTrend);
        setLoading(false);
        setChartsLoading(false);
      }
    );
  };

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <MonitoringSubNav activeKey='overview' />

      <KoToolBar
        title='Monitoring Overview'
        search={{ visible: false }}
        filter={{ visible: false, options: [] }}
        buttonList={[
          {
            label: 'Refresh',
            icon: <KoIcon color='#565555' name='ReloadOutlined' width={16} height={16} />,
            onClick: fetchDashboard
          }
        ]}
      />

      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        {STAT_CARDS.map((card) => (
          <div key={card.key} className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: card.bg }}>
              <KoIcon name={card.icon} width={22} height={22} />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{dashboard[card.key] ?? 0}</span>
              <span className={styles.statLabel}>{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row 1 — Two columns */}
      <div className={styles.chartsRow}>
        <div className={styles.chartCard}>
          <KoTitle level={5} text='Error Rate Over Time' className={styles.chartTitle} />
          {chartsLoading && <KoSkeleton active paragraph={{ rows: 8 }} />}
          {!chartsLoading && <KoCharts option={errorRateChart(errorData)} />}
        </div>
        <div className={styles.chartCard}>
          <KoTitle level={5} text='Alert Timeline' className={styles.chartTitle} />
          {chartsLoading && <KoSkeleton active paragraph={{ rows: 8 }} />}
          {!chartsLoading && <KoCharts option={alertTimelineChart(alertData)} />}
        </div>
      </div>

      {/* Charts Row 2 — Full width */}
      <div className={styles.chartsRow}>
        <div className={`${styles.chartCard} ${styles.chartFullWidth}`}>
          <KoTitle level={5} text='Incident Trend by Severity' className={styles.chartTitle} />
          {chartsLoading && <KoSkeleton active paragraph={{ rows: 8 }} />}
          {!chartsLoading && <KoCharts option={incidentTrendChart(incidentData)} />}
        </div>
      </div>
    </div>
  );
}

export default MonitoringOverview;
